import axios from "axios";

const {REACT_APP_CREATE_SESSION_API_URL} = process.env;


// ============================================================
// STORAGE KEYS
// ============================================================

const JWE_STORAGE_KEY = "nea.legacy.token";

const SESSION_STORAGE_KEY = "nea.session";


// ============================================================
// HTTP HEADERS
// ============================================================

const NEA_SESSION_HEADER = "X-NEA-SESSION-ID";


// ============================================================
// JWE
// ============================================================

const WORKFLOW_STORAGE_KEY = "nea.workflow";

/**
 * Un JWE compact est composé de 5 parties :
 *
 * header.encryptedKey.iv.ciphertext.tag
 */
const JWE_PATTERN =
  /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]*){4}$/;


/**
 * Retourne le JWE actuellement stocké.
 */
export const getJwe = () => {
  return sessionStorage.getItem(JWE_STORAGE_KEY);
};


/**
 * Supprime le JWE courant.
 */
export const clearJwe = () => {
  sessionStorage.removeItem(JWE_STORAGE_KEY);
};


// ============================================================
// NEA SESSION
// ============================================================

/**
 * Retourne la session NEA courante.
 */
export const getNeaSession = () => {

  const raw =
    sessionStorage.getItem(
      SESSION_STORAGE_KEY
    );

  if (!raw) {
    return null;
  }

  try {

    return JSON.parse(raw);

  } catch (e) {

    // Session corrompue
    clearNeaSession();

    return null;
  }
};


/**
 * Stocke la session NEA.
 */
export const setNeaSession = (session) => {

  if (!session) {
    return;
  }

  sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify(session)
  );
};


/**
 * Supprime la session NEA courante.
 */
export const clearNeaSession = () => {

  sessionStorage.removeItem(
      SESSION_STORAGE_KEY
  );

  sessionStorage.removeItem(
      WORKFLOW_STORAGE_KEY
  );
};


// ============================================================
// CAPTURE JWE FROM URL
// ============================================================

/**
 * Capture le JWE présent en dernier segment du hash.
 *
 * Exemple :
 *
 * #/chequeculture/create/<JWE>
 *
 * Le JWE est :
 *  - stocké dans sessionStorage
 *  - retiré de l'URL
 *
 * IMPORTANT :
 *
 * Si un JWE différent est reçu alors qu'un ancien JWE
 * existe déjà dans le navigateur, on considère qu'il s'agit
 * d'un nouveau contexte d'authentification.
 *
 * Dans ce cas, l'ancienne session NEA est supprimée.
 */
export const captureJweFromUrl = () => {

  const hash =
    window.location.hash || "";


  const queryIndex =
    hash.indexOf("?");


  const path =
    queryIndex >= 0
      ? hash.substring(0, queryIndex)
      : hash;


  const query =
    queryIndex >= 0
      ? hash.substring(queryIndex)
      : "";


  const segments =
    path.split("/");


  const lastSegment =
    segments[segments.length - 1];


  // Aucun JWE dans l'URL
  if (
    !lastSegment ||
    !JWE_PATTERN.test(lastSegment)
  ) {
    return null;
  }


  const previousJwe =
    getJwe();


  // ==========================================================
  // NEW JWE DETECTED
  // ==========================================================

  if (
    previousJwe &&
    previousJwe !== lastSegment
  ) {

    /*
     * Le navigateur peut conserver sessionStorage
     * lorsqu'on revient avec BACK.
     *
     * Exemple :
     *
     * Agent A
     *   JWE A
     *   session 101
     *
     * BACK
     *
     * Agent B
     *   JWE B
     *   ancienne session 101 encore présente
     *
     * => surtout ne pas réutiliser session 101.
     */
    clearNeaSession();
  }


  // Stocke le nouveau JWE
  sessionStorage.setItem(
    JWE_STORAGE_KEY,
    lastSegment
  );


  // Retire le JWE de l'URL
  segments.pop();


  const newHash =
    segments.join("/") + query;


  window.history.replaceState(
    null,
    "",
    window.location.pathname +
      window.location.search +
      newHash
  );


  return lastSegment;
};


// ============================================================
// API
// ============================================================

/**
 * Détermine si l'appel est une API NEA.
 *
 * Tous les appels /api sont protégés par le JWE.
 */
const isNeaApiCall = (url) => {
  return !!url && url.includes("/api");
};


// ============================================================
// AXIOS REQUEST INTERCEPTOR
// ============================================================

export const installJweInterceptor = () => {

  axios.interceptors.request.use(

    (config) => {

      const jwe =
        getJwe();

      const neaSession =
        getNeaSession();

      const workflowId =
        getNeaWorkflow();


      // ======================================================
      // JWE
      // ======================================================

      if (
        jwe &&
        isNeaApiCall(config.url)
      ) {

        config.headers = {
          ...config.headers,

          Authorization:
            "Bearer " + jwe
        };
      }


      // ======================================================
      // NEA SESSION
      // ======================================================

      const isCreateSessionCall =
        config.url &&
        config.url.includes(
          REACT_APP_CREATE_SESSION_API_URL
        );


      if (
        neaSession &&
        neaSession.sessionId &&
        isNeaApiCall(config.url) &&
        !isCreateSessionCall
      ) {

        config.headers = {
          ...config.headers,

          "X-NEA-SESSION-ID":
            neaSession.sessionId
        };
      }


      // ======================================================
      // NEA WORKFLOW
      // ======================================================

      if (
        workflowId &&
        isNeaApiCall(config.url) &&
        !isCreateSessionCall
      ) {

        config.headers = {
          ...config.headers,

          "X-NEA-WORKFLOW-ID":
            workflowId
        };
      }


      return config;
    },


    (error) => {
      return Promise.reject(error);
    }
  );
};




// ============================================================
// JWE EXPIRATION
// ============================================================

export const JWE_EXPIRED_EVENT =
  "nea:jwe-expired";


/**
 * Abonnement à l'événement d'expiration du JWE.
 */
export const onJweExpired = (callback) => {

  window.addEventListener(
    JWE_EXPIRED_EVENT,
    callback
  );


  return () => {

    window.removeEventListener(
      JWE_EXPIRED_EVENT,
      callback
    );
  };
};


// ============================================================
// AXIOS RESPONSE INTERCEPTOR
// ============================================================

/**
 * Intercepte les 401 liés au JWE.
 *
 * IMPORTANT :
 *
 * Un 401 sur un appel portant le Bearer signifie ici
 * que le JWE est invalide/expiré selon le comportement
 * du LegacyJweBearerFilter.
 */
export const installJweExpiredInterceptor = () => {

  axios.interceptors.response.use(

    (response) => {
      return response;
    },


    (error) => {

      const config =
        error.config || {};


      const status =
        error.response &&
        error.response.status;


      const bearerSent =
        !!(
          config.headers &&
          config.headers.Authorization
        );


      if (
        status === 401 &&
        isNeaApiCall(config.url) &&
        bearerSent
      ) {

        /*
         * Le JWE n'est plus valide.
         *
         * La session NEA associée ne doit plus
         * être conservée côté front.
         */
        clearJwe();

        clearNeaSession();


        window.dispatchEvent(
          new CustomEvent(
            JWE_EXPIRED_EVENT
          )
        );
      }


      return Promise.reject(error);
    }
  );
};


// ============================================================
// DEVICE INFORMATION
// ============================================================

const DEVICE_ID_STORAGE_KEY = "nea.device.id";


/**
 * Retourne un identifiant stable pour ce navigateur.
 *
 * Il est conservé dans localStorage :
 * - fermeture de l'onglet : conservé
 * - fermeture du navigateur : conservé
 * - sessionStorage : non
 *
 * IMPORTANT :
 * Ce deviceId n'est PAS une donnée de sécurité.
 * Il sert uniquement à identifier le contexte navigateur/device.
 */
export const getNeaDeviceId = () => {

  let deviceId =
    localStorage.getItem(
      DEVICE_ID_STORAGE_KEY
    );


  if (!deviceId) {

    if (
      window.crypto &&
      window.crypto.randomUUID
    ) {

      deviceId =
        window.crypto.randomUUID();

    } else {

      // Fallback pour anciens navigateurs
      deviceId =
        "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
          .replace(/[xy]/g, (c) => {

            const r =
              Math.random() * 16 | 0;

            const v =
              c === "x"
                ? r
                : (r & 0x3 | 0x8);

            return v.toString(16);
          });
    }


    localStorage.setItem(
      DEVICE_ID_STORAGE_KEY,
      deviceId
    );
  }


  return deviceId;
};


/**
 * Détermine grossièrement le type de device.
 *
 * Ce n'est pas une donnée de sécurité.
 */
const getDeviceType = () => {

  const width =
    window.innerWidth;


  if (
    /Mobi|Android/i.test(
      navigator.userAgent
    )
  ) {

    return "MOBILE";
  }


  if (
    /Tablet|iPad/i.test(
      navigator.userAgent
    ) ||
    (width >= 768 && width <= 1024)
  ) {

    return "TABLET";
  }


  return "DESKTOP";
};


/**
 * Récupère les informations navigateur/device.
 *
 * Toutes les informations sont optionnelles.
 * Si un navigateur ne fournit pas une donnée,
 * on envoie null.
 */
export const getNeaDeviceMetadata = () => {

  const userAgent =
    navigator.userAgent || null;


  let browser = null;
  let browserVersion = null;


  // ----------------------------------------------------------
  // Modern browsers : User-Agent Client Hints
  // ----------------------------------------------------------

  if (
    navigator.userAgentData &&
    navigator.userAgentData.brands
  ) {

    const brands =
      navigator.userAgentData.brands;


    const browserBrand =
      brands.find((brand) =>
        !/Not.?A.?Brand/i.test(
          brand.brand
        )
      );


    if (browserBrand) {

      browser =
        browserBrand.brand;

      browserVersion =
        browserBrand.version;
    }
  }


  // ----------------------------------------------------------
  // Fallback
  // ----------------------------------------------------------

  if (!browser) {

    const ua =
      userAgent || "";


    if (/Edg\/([\d.]+)/i.test(ua)) {

      browser = "Edge";

      browserVersion =
        ua.match(/Edg\/([\d.]+)/i)[1];

    } else if (/Chrome\/([\d.]+)/i.test(ua)) {

      browser = "Chrome";

      browserVersion =
        ua.match(/Chrome\/([\d.]+)/i)[1];

    } else if (/Firefox\/([\d.]+)/i.test(ua)) {

      browser = "Firefox";

      browserVersion =
        ua.match(/Firefox\/([\d.]+)/i)[1];

    } else if (/Version\/([\d.]+).*Safari/i.test(ua)) {

      browser = "Safari";

      browserVersion =
        ua.match(/Version\/([\d.]+).*Safari/i)[1];

    } else {

      browser = null;
      browserVersion = null;
    }
  }


  return {

    deviceId:
      getNeaDeviceId(),

    deviceType:
      getDeviceType(),

    browser:
      browser,

    browserVersion:
      browserVersion,

    operatingSystem:
      navigator.platform || null,

    userAgent:
      userAgent,

    clientLanguage:
      navigator.language || null,

    timezone:
      (
        Intl.DateTimeFormat().resolvedOptions().timeZone
      ) || null
  };
};




export const initializeNeaSession = async () => {

  const existing =
    getNeaSession();

  if (
    existing &&
    existing.sessionId
  ) {
    return existing;
  }


  // Workflow initial
  const workflowId =
    "INITIALIZATION";
  setNeaWorkflow(
    workflowId
  );

  const deviceMetadata =
    getNeaDeviceMetadata();
  const response =
    await axios.post(
      REACT_APP_CREATE_SESSION_API_URL,
      {
        deviceId:
          deviceMetadata.deviceId,

        workflowId:
          workflowId,

        deviceType:
          deviceMetadata.deviceType,

        browser:
          deviceMetadata.browser,

        browserVersion:
          deviceMetadata.browserVersion,

        operatingSystem:
          deviceMetadata.operatingSystem,

        clientLanguage:
          deviceMetadata.clientLanguage,

        timezone:
          deviceMetadata.timezone
      }
    );


  const session =
    response.data;


  if (
    !session ||
    !session.sessionId
  ) {
    throw new Error(
      "Invalid NEA session response"
    );
  }


  setNeaSession(
    session
  );


  return session;
};





export const getNeaWorkflow = () => {

  return sessionStorage.getItem(
    WORKFLOW_STORAGE_KEY
  );
};


export const setNeaWorkflow = (workflowId) => {

  if (
    !workflowId ||
    typeof workflowId !== "string"
  ) {
    return;
  }

  sessionStorage.setItem(
    WORKFLOW_STORAGE_KEY,
    workflowId
  );
};


